import { useCallback } from 'react';
import { useSimulationContext } from '../context/SimulationContext';
import { calculateKinematics } from '../utils/physicsUtils';
import { PhysicsEngine } from '../utils/physicsEngine';
import { MATERIALS } from '../data/physicsConstants';

/**
 * Custom hook for running MCS tomography simulations with the context
 */
export const useSimulation = () => {
  const { state, actions } = useSimulationContext();

  const runSimulation = useCallback(async () => {
    if (state.simulation.running) return;

    console.log("Starting simulation...");
    actions.startSimulation();

    // Get material X0 values
    const mat = MATERIALS[state.material] || MATERIALS.pvc;
    const insertMat = state.insertMaterial ? MATERIALS[state.insertMaterial] : null;
    console.log("Material:", mat.name, "Insert:", insertMat?.name);

    // Prepare MCS simulation parameters
    const params = {
      momentum: state.beam.momentum,
      mass: state.particle.mass, // Pass mass explicitly
      material: state.material,
      materialX0: mat.X0, // Pass X0 explicitly
      phantomThickness: state.phantomThickness,
      phantomSize: state.phantomSize || 3,
      beamIntensity: state.beam.intensity,
      insertMaterial: state.insertMaterial,
      insertMaterialX0: insertMat?.X0, // Pass Insert X0 explicitly
      insertRadius: 1.0, 
      seed: state.rngSeed,
      detectorResponse: state.detectorResponse,
    };
    
    let watchdogTimer;

    try {
      // Watchdog: Force fail after 10 seconds (increased for async)
      watchdogTimer = setTimeout(() => {
          console.error("Simulation timed out!");
          actions.simulationError("Simulation timed out (engine unresponsive).");
      }, 10000);

      const engine = new PhysicsEngine(params);
      console.log("Starting engine run...");
      
      // Run async with progress callback
      const rawResults = await engine.runAsync((progress) => {
        actions.updateProgress(progress);
      });
      
      console.log("Engine run complete");

      // Generate MCS angle vs thickness curve
      const curveData = generateMCSCurve(
        state.beam.momentum,
        state.particle.mass,
        mat.X0,
        insertMat?.X0
      );

      const results = {
        ...rawResults,
        curveData,
        config: {
          particle: state.particle,
          momentum: state.beam.momentum,
          material: mat.name,
          materialX0: mat.X0,
          insertMaterial: insertMat?.name || null,
          insertX0: insertMat?.X0 || null,
          phantomThickness: state.phantomThickness,
          phantomSize: state.phantomSize,
          seed: state.rngSeed,
          detectorResponse: state.detectorResponse,
        },
        timestamp: new Date().toISOString(),
      };

      actions.updateProgress(100);
      
      // Small delay for visual completion
      await new Promise(resolve => setTimeout(resolve, 200));
      actions.completeSimulation(results);

    } catch (error) {
      console.error("Simulation error:", error);
      actions.simulationError(error.message);
    } finally {
      if (watchdogTimer) clearTimeout(watchdogTimer);
    }
  }, [state, actions]);

  const exportResults = useCallback((format = 'csv') => {
    const results = state.simulation.results;
    if (!results) return null;

    if (format === 'csv') {
      let csv = 'Event ID,X (cm),Y (cm),Momentum (GeV/c),Theta_X (mrad),Theta_Y (mrad),Theta (mrad),Material,Detected\n';
      csv += `# RNG Seed: ${results.seed}\n`;
      csv += `# Material: ${results.config?.material || 'Unknown'}\n`;
      csv += `# Phantom Thickness: ${results.config?.phantomThickness} cm\n`;
      csv += `# Beam Momentum: ${results.config?.momentum} GeV/c\n`;
      csv += `# Detector Response: ${results.detectorResponseEnabled ? 'Enabled' : 'Disabled'}\n`;
      results.events.forEach(e => {
        csv += `${e.id},${e.x?.toFixed(3) || 0},${e.y?.toFixed(3) || 0},${e.momentum?.toFixed(4) || 0},${e.thetaX?.toFixed(4) || 0},${e.thetaY?.toFixed(4) || 0},${e.theta?.toFixed(4) || 0},${e.material || 'Unknown'},${e.detected}\n`;
      });
      return csv;
    } else if (format === 'json') {
      return JSON.stringify(results, null, 2);
    }
    return null;
  }, [state.simulation.results]);


  const downloadResults = useCallback((format = 'csv') => {
    const data = exportResults(format);
    if (!data) return;

    const blob = new Blob([data], {
      type: format === 'csv' ? 'text/csv' : 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zscan_mcs_results_${Date.now()}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [exportResults]);

  return {
    state,
    actions,
    runSimulation,
    exportResults,
    downloadResults,
    isRunning: state.simulation.running,
    progress: state.simulation.progress,
    results: state.simulation.results,
  };
};

/**
 * Generate theoretical MCS angle vs thickness curve using Highland formula
 */
function generateMCSCurve(momentum, mass, X0, insertX0 = null) {
  const points = [];
  const maxThickness = 10; // cm

  for (let L = 0.1; L <= maxThickness; L += 0.5) {
    // Highland formula: θ = (13.6 MeV / βcp) × √(L/X₀) × [1 + 0.038 × ln(L/X₀)]
    const { beta } = calculateKinematics(momentum, mass);
    const betaP = beta * momentum; // GeV/c

    const ratio = L / X0;
    const logCorr = 1 + 0.038 * Math.log(ratio);
    const theta = (0.0136 / betaP) * Math.sqrt(ratio) * logCorr;
    const thetaMrad = theta * 1000;

    const point = {
      thickness: parseFloat(L.toFixed(1)),
      thetaRMS: parseFloat(thetaMrad.toFixed(3)),
      variance: parseFloat((thetaMrad * thetaMrad).toFixed(4)),
    };

    // Add insert material curve if provided
    if (insertX0) {
      const ratioInsert = L / insertX0;
      const logCorrInsert = 1 + 0.038 * Math.log(ratioInsert);
      const thetaInsert = (0.0136 / betaP) * Math.sqrt(ratioInsert) * logCorrInsert;
      const thetaInsertMrad = thetaInsert * 1000;
      point.thetaRMSInsert = parseFloat(thetaInsertMrad.toFixed(3));
      point.varianceInsert = parseFloat((thetaInsertMrad * thetaInsertMrad).toFixed(4));
    }

    points.push(point);
  }

  return points;
}

export default useSimulation;
